// Extrait de ClinicAdminDashboard.js (handover bloc F3-B) — présentationnel : filtres, handlers et masquage reset mdp inchangés.
import { Search, Users, Archive, Plus, Clock, Edit2, Key, RefreshCw, Trash2 } from "lucide-react";
import { ROLES } from "@/components/admin/modals/ClinicAdminModals";

export default function ClinicTeamTab({
  filteredUsers,
  inactiveCount,
  userFilter,
  onUserFilterChange,
  showInactiveUsers,
  onToggleInactive,
  onOpenCreate,
  onEdit,
  onResetPassword,
  onDeactivate,
  onRestore,
  onDeleteRequest,
}) {
  return (
    <div className="space-y-4">
      {/* Actions & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={userFilter.search}
              onChange={e => onUserFilterChange(f => ({ ...f, search: e.target.value }))}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64"
            />
          </div>
          <select
            value={userFilter.role}
            onChange={e => onUserFilterChange(f => ({ ...f, role: e.target.value }))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous les rôles</option>
            {ROLES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {inactiveCount > 0 && (
            <button
              onClick={onToggleInactive}
              data-testid="toggle-inactive-users"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                showInactiveUsers
                  ? "border-amber-300 bg-amber-50 text-amber-700"
                  : "border-slate-200 text-slate-500 hover:border-slate-300"
              }`}
            >
              <Archive className="w-4 h-4" />
              Archivés ({inactiveCount})
            </button>
          )}
          <button
            onClick={onOpenCreate}
            data-testid="add-user-btn"
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="dash-card overflow-x-auto p-0">
        <table className="w-full min-w-[800px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Utilisateur</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Email</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Rôle</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Dernière connexion</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Statut</th>
              <th className="text-right p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold">
                      {u.firstName?.[0]}{u.lastName?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{u.firstName} {u.lastName}</p>
                      {u.phone && <p className="text-xs text-slate-400">{u.phone}</p>}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-sm text-slate-600">{u.email}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    u.role === "MEDECIN" ? "bg-blue-100 text-blue-700" :
                    u.role === "IDE" ? "bg-pink-100 text-pink-700" :
                    u.role === "ADMIN_CLINIQUE" ? "bg-purple-100 text-purple-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {ROLES.find(r => r.value === u.role)?.label || u.role}
                  </span>
                </td>
                <td className="p-4" data-testid={`last-login-${u.id}`}>
                  {u.lastLoginAt ? (
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span>{new Date(u.lastLoginAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      <span className="text-xs text-slate-400">{new Date(u.lastLoginAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Jamais connecté</span>
                  )}
                </td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    u.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {u.isActive ? "Actif" : "Inactif"}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    {u.isActive ? (
                      <>
                        <button
                          onClick={() => onEdit(u)}
                          data-testid={`edit-user-${u.id}`}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {!["ADMIN_CLINIQUE", "ADMIN_GROUPE", "SUPER_ADMIN_SAAS"].includes(u.role) && (
                        <button
                          onClick={() => onResetPassword(u)}
                          data-testid={`reset-pwd-${u.id}`}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Réinitialiser mot de passe"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        )}
                        <button
                          onClick={() => onDeactivate(u.id)}
                          data-testid={`deactivate-user-${u.id}`}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Désactiver"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onRestore(u.id)}
                          data-testid={`restore-user-${u.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                          title="Restaurer"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Restaurer
                        </button>
                        <button
                          onClick={() => onDeleteRequest(u)}
                          data-testid={`permanent-delete-user-${u.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors"
                          title="Supprimer définitivement"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucun utilisateur trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
}
